<template>
  <div>
    <!-- SubKrewe Association Card -->
    <AdminMemberAssociationCard
      v-if="canManageAssignment"
      :title="isCaptain ? 'Captain of' : 'Member of'"
      :subkrewe="associatedSubkrewe"
      :member-count="memberCount"
      :is-loading="isSubkreweLoading"
      :is-captain="isCaptain"
      :empty-message="`No SubKrewe assigned to this ${isCaptain ? 'captain' : 'member'}`"
      :assign-button-label="'Assign to SubKrewe'"
      :remove-tooltip="isCaptain ? 'Remove Captain from SubKrewe' : 'Remove from SubKrewe'"
      :is-removing="isRemoving"
      @view-subkrewe="viewSubkreweDetails"
      @remove-from-subkrewe="removeFromSubkrewe"
      @show-assign-dialog="showAssignDialog(false)"
    />

    <!-- Read-only SubKrewe info for redshirt admins viewing non-redshirts -->
    <div
      v-else-if="associatedSubkrewe"
      class="kb-row"
    >
      <div class="min-w-0">
        <h4 class="kb-row-title">
          {{ isCaptain ? 'Captain of' : 'Member of' }}
        </h4>
        <p class="mt-0.5 text-sm text-kb-fg">
          {{ associatedSubkrewe.name }}
        </p>
        <p class="kb-row-meta mt-1">
          Read-only for red shirt admins
        </p>
      </div>
    </div>

    <!-- SubKrewe assignment dialogs -->
    <AdminMemberSearchDialog
      :visible="showAssignSubkreweDialog"
      title="Assign SubKrewe"
      search-label="Select a SubKrewe to captain"
      placeholder="Type SubKrewe name"
      :filter-function="filterAllSubkrewes"
      @update:visible="onSubkreweDialogVisibilityChange"
      @item-selected="assignSelectedSubKrewe"
    />

    <AdminMemberSearchDialog
      :visible="showAssignMemberSubkreweDialog"
      title="Assign to SubKrewe"
      search-label="Select a SubKrewe to join"
      placeholder="Type SubKrewe name"
      :filter-function="filterAllSubkrewes"
      @update:visible="showAssignMemberSubkreweDialog = $event"
      @item-selected="assignSelectedMemberSubKrewe"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, ref } from 'vue'
import { useCurrentUser } from 'vuefire'
import { AdminSubKreweDrawerContent } from '#components'
import type { Member } from '~/shared/types/member'
import type { SubKrewe } from '~/shared/types/subkrewe'
import { searchSubKrewes } from '~/utils/subkreweSearch'

interface Props {
  member: Member
  currentMemberType: string
  associatedSubkrewe: SubKrewe | null
  isRedshirtAdmin?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['member-updated', 'assignment-complete', 'captain-promotion-cancelled'])

const membersStore = useMembersStore()
const subkrewesStore = useSubKrewesStore()
const toast = useToast()
const confirm = useConfirm()

// Additional computed properties moved from DrawerContent
const isCaptain = computed(() => props.currentMemberType === 'captain')
const isRedshirt = computed(() => props.currentMemberType === 'redshirt')
const isSubkreweLoading = computed(() => subkrewesStore.loading)
const memberCount = computed(() => props.associatedSubkrewe?.members?.length || 0)

// Redshirt admins can only manage redshirts
const canManageAssignment = computed(() => {
  if (!props.isRedshirtAdmin) return true
  return isRedshirt.value
})

// Assignment dialogs state
const showAssignSubkreweDialog = ref(false)
const selectedSubkrewe = ref<SubKrewe | null>(null)
const showAssignMemberSubkreweDialog = ref(false)
const selectedMemberSubkrewe = ref<SubKrewe | null>(null)
const pendingMemberTypeChange = ref<'member' | 'redshirt' | 'captain' | null>(null)
const pendingRoleChange = ref(false)

// SubKrewe details view - moved from DrawerContent
const viewSubkreweDetails = () => {
  if (!props.associatedSubkrewe) return

  const { setInternalNavigation, openDrawer } = useDrawer()
  setInternalNavigation(true)

  openDrawer({
    title: props.associatedSubkrewe.name || 'SubKrewe Details',
    data: props.associatedSubkrewe,
    component: markRaw(AdminSubKreweDrawerContent),
  })
}

// The removal is saving: the card's button spins only then, not while the
// confirm is open (closing that with Esc or X used to leave it spinning)
const isRemoving = ref(false)

// Remove member from subkrewe - moved from DrawerContent
const removeFromSubkrewe = async () => {
  if (!props.associatedSubkrewe) return

  // IMPORTANT: Always use the current member data from store
  const memberPath = `members/${props.member.id}`
  const currentMember = membersStore.members[memberPath]
  const subkreweName = props.associatedSubkrewe.name || 'this SubKrewe'
  const currentUser = useCurrentUser().value

  confirm.require({
    message: isCaptain.value
      ? `Remove ${props.member.name} as captain of "${subkreweName}"?`
      : `Remove ${props.member.name} from "${subkreweName}"?`,
    header: isCaptain.value ? 'Remove Captain' : 'Remove from SubKrewe',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      isRemoving.value = true
      try {
        let success = false

        if (isCaptain.value) {
          success = await subkrewesStore.removeCaptainFromSubKrewe(
            props.associatedSubkrewe!,
            currentMember || props.member,
            false,
          )
        }
        else {
          // Explicitly pass current user ID to ensure proper notification handling
          success = await subkrewesStore.removeMemberFromSubKrewe(
            props.associatedSubkrewe!,
            currentMember || props.member,
            currentUser?.uid, // Explicitly pass the current user ID
            'removed by administrator', // Add reason to clarify this was an admin action
          )
        }

        if (success) {
          await nextTick()

          // Signal completion
          emit('assignment-complete')

          toast.add({
            severity: 'success',
            summary: isCaptain.value ? 'Captain Removed' : 'Member Removed',
            detail: isCaptain.value
              ? `${props.member.name} is no longer captain of "${subkreweName}"`
              : `${props.member.name} has been removed from "${subkreweName}"`,
            life: 3000,
          })
        }
        else {
          toast.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to remove ${isCaptain.value ? 'captain' : 'member'}`,
            life: 3000,
          })
        }
      }
      catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: `${error}`,
          life: 3000,
        })
      }
      finally {
        isRemoving.value = false
      }
    },
  })
}

// Show assignment dialog
const showAssignDialog = (isCaptainPromoting = false) => {
  if (isCaptainPromoting) {
    pendingMemberTypeChange.value = 'captain'
    pendingRoleChange.value = true
  }

  if (isCaptain.value || isCaptainPromoting) {
    showAssignSubkreweDialog.value = true
  }
  else {
    showAssignMemberSubkreweDialog.value = true
  }
}

// Filter subkrewes for dialog
const filterAllSubkrewes = (searchTerm: string) =>
  searchSubKrewes(subkrewesStore.subkrewesArray, searchTerm)

// Handle dialog visibility change
const onSubkreweDialogVisibilityChange = (isVisible: boolean) => {
  showAssignSubkreweDialog.value = isVisible

  // If dialog is being closed and we had a pending captain promotion
  if (!isVisible && pendingMemberTypeChange.value === 'captain') {
    emit('captain-promotion-cancelled')

    toast.add({
      severity: 'info',
      summary: 'Cancelled',
      detail: 'Captain promotion cancelled - no SubKrewe selected',
      life: 3000,
    })

    // Reset pending states
    pendingMemberTypeChange.value = null
    pendingRoleChange.value = false
  }
}

// Assign captain to subkrewe
const assignCaptainToSubkrewe = async (): Promise<boolean> => {
  if (!selectedSubkrewe.value) return false

  // IMPORTANT: Always use the current member data from store
  const memberPath = `members/${props.member.id}`
  const currentMember = membersStore.members[memberPath]

  try {
    const success = await subkrewesStore.assignCaptainToSubKrewe(
      selectedSubkrewe.value,
      currentMember || props.member,
    )

    if (success) {
      // Wait for Vue to process changes
      await nextTick()

      // Signal completion
      emit('assignment-complete')

      toast.add({
        severity: 'success',
        summary: 'SubKrewe Assigned',
        detail: `${props.member.name} is now captain of "${selectedSubkrewe.value?.name || 'this SubKrewe'}"`,
        life: 3000,
      })

      // Reset the UI state
      showAssignSubkreweDialog.value = false
      selectedSubkrewe.value = null
      return true
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to assign SubKrewe',
        life: 3000,
      })
      return false
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
    return false
  }
}

// Assign member to subkrewe
const assignMemberToSubkrewe = async () => {
  if (!selectedMemberSubkrewe.value) return

  // IMPORTANT: Always use the current member data from store
  const memberPath = `members/${props.member.id}`
  const currentMember = membersStore.members[memberPath]

  const subkreweName = selectedMemberSubkrewe.value.name

  try {
    let success = false

    // For regular members
    if (props.currentMemberType !== 'captain') {
      success = await subkrewesStore.addMemberToSubKrewe(
        selectedMemberSubkrewe.value,
        currentMember || props.member,
      )
    }
    // For captains
    else {
      success = await subkrewesStore.assignCaptainToSubKrewe(
        selectedMemberSubkrewe.value,
        currentMember || props.member,
      )
    }

    if (success) {
      // Wait for Vue to process changes
      await nextTick()

      // Signal completion
      emit('assignment-complete')

      toast.add({
        severity: 'success',
        summary: props.currentMemberType === 'captain' ? 'Captain Assigned' : 'Member Assigned',
        detail: `${props.member.name || 'Member'} has been assigned to "${subkreweName}"`,
        life: 3000,
      })

      // Close dialog
      showAssignMemberSubkreweDialog.value = false
      selectedMemberSubkrewe.value = null
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to assign to SubKrewe',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
}

// Selected subkrewe handler - for captain dialog
const assignSelectedSubKrewe = async (subkrewe: SubKrewe) => {
  selectedSubkrewe.value = subkrewe

  try {
    let success = false

    // If this is part of a captain promotion flow, do the member type change first
    if (pendingMemberTypeChange.value === 'captain') {
      // Change member type to captain and assign to the SubKrewe in one operation
      success = await membersStore.promoteToCaptain(props.member, subkrewe)
    }
    else {
      // Regular SubKrewe assignment for an existing captain
      success = await assignCaptainToSubkrewe()
    }

    if (success) {
      // Force refresh store data
      await subkrewesStore.fetchSubKrewe(subkrewe.id)
      await membersStore.fetchMember(props.member.id)

      // Signal completion
      emit('member-updated')

      toast.add({
        severity: 'success',
        summary: 'Captain Assigned',
        detail: `${props.member.name} is now captain of "${subkrewe.name}"`,
        life: 3000,
      })
    }
  }
  finally {
    // Reset UI state
    showAssignSubkreweDialog.value = false
    selectedSubkrewe.value = null
    pendingRoleChange.value = false
    pendingMemberTypeChange.value = null
  }
}

// Selected subkrewe handler - for member dialog
const assignSelectedMemberSubKrewe = (subkrewe: SubKrewe) => {
  selectedMemberSubkrewe.value = subkrewe
  assignMemberToSubkrewe()
}

// Add a method to reset dialog state without showing the dialog
const resetDialogState = () => {
  showAssignSubkreweDialog.value = false
  showAssignMemberSubkreweDialog.value = false
  selectedSubkrewe.value = null
  selectedMemberSubkrewe.value = null
  pendingMemberTypeChange.value = null
  pendingRoleChange.value = false
}

// Expose methods to parent component
defineExpose({
  showAssignDialog,
  resetDialogState,
})
</script>
